import React from "react";
import ReactDOM from "react-dom";
import SitecoreFormUploadModal from "./SitecoreFormUploadModal.jsx";

/**
 * Hooks jQuery's XHR factory so we can read native upload progress during a
 * Sitecore Experience Forms AJAX submit.
 *
 * We must override the `xhr` factory (not listen to `ajaxSend`) because the
 * object jQuery passes to `ajaxSend` is the jqXHR wrapper, which does NOT expose
 * `.upload`. Only the native XMLHttpRequest returned by the factory has it.
 *
 * The progress listener is attached to every request's native XHR but only
 * emits events while an upload is active (window.__threadFormUploadActive),
 * which is set by the modal when the form submit begins. This keeps timing
 * correct: the factory runs before our begin callback, but progress events fire
 * during transfer, by which point the flag is set.
 */
function wireXHRUploadProgress(jq) {
  if (window.__threadFormUploadProgressWired) return;
  if (!jq || !jq.ajaxSettings || typeof jq.ajaxSettings.xhr !== "function") {
    return;
  }
  window.__threadFormUploadProgressWired = true;

  const defaultXhrFactory = jq.ajaxSettings.xhr;

  jq.ajaxSetup({
    xhr: function () {
      const xhr = defaultXhrFactory.call(this);

      if (xhr && xhr.upload) {
        xhr.upload.addEventListener("progress", function (ev) {
          if (!window.__threadFormUploadActive || !ev.lengthComputable) return;

          const percent = Math.round((ev.loaded / ev.total) * 100);
          window.dispatchEvent(
            new CustomEvent("thread:form-upload:progress", {
              detail: { percent },
            }),
          );

          if (ev.loaded >= ev.total) {
            window.dispatchEvent(
              new CustomEvent("thread:form-upload:processing"),
            );
          }
        });
      }

      return xhr;
    },
  });
}

/**
 * Waits for the page-level jQuery (used by Sitecore's unobtrusive AJAX) to be
 * available, then wires upload progress. Sitecore registers jQuery on the page;
 * our bundle may run slightly earlier, so we poll briefly.
 */
function whenJQueryReady(callback, attempts) {
  const tries = attempts || 0;

  if (window.jQuery && window.jQuery.ajaxSettings) {
    callback(window.jQuery);
    return;
  }

  if (tries > 100) return; // ~10s; give up quietly (no file forms on page)

  window.setTimeout(function () {
    whenJQueryReady(callback, tries + 1);
  }, 100);
}

export function initSitecoreFormUploadProgress() {
  if (document.getElementById("sitecore-form-upload-modal-root")) return;

  const host = document.createElement("div");
  host.id = "sitecore-form-upload-modal-root";
  document.body.appendChild(host);

  ReactDOM.render(<SitecoreFormUploadModal />, host);

  whenJQueryReady(wireXHRUploadProgress);
}
